import { useRef } from "react";
import { uploadAudio } from "../api/word";
import { useBaseRecorder } from "./useBaseRecorder";

export const useWordRecorder = ({
  onStatusChange,
  autoStopOnSilence = true,
  silenceThreshold = 5,
  silenceTimeout = 1000,
  ignoreInitialSilence = 2000,
  maxRecordTime = 10000,
  } = {}) => {
  const { startBaseRecording, stopBaseRecording, getAnalyser, cleanup } = useBaseRecorder({ onStatusChange });

  const isSpokenRef = useRef(false);
  const silenceLoopRef = useRef(null);
  const voiceLoopRef = useRef(false);
  const maxTimeoutRef = useRef(null);
  const isCancelledRef = useRef(false); // 취소 상태 플래그
  const startPromiseResolveRef = useRef(null);

  const detectVoice = () => {
    const analyser = getAnalyser();
    if (!analyser) return;

    voiceLoopRef.current = true;
    const dataArray = new Uint8Array(analyser.fftSize);

    const loop = () => {
      if (isCancelledRef.current || !voiceLoopRef.current) return;
      analyser.getByteFrequencyData(dataArray);
      const volume = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;

      if (volume > 10) {
        isSpokenRef.current = true;
        onStatusChange?.("listening");
      }

      requestAnimationFrame(loop);
    };

    requestAnimationFrame(loop);
  };

  const detectSilence = (onSilence) => {
    const analyser = getAnalyser();
    if (!analyser) return;
    const dataArray = new Uint8Array(analyser.fftSize);
    let silenceStart = performance.now();
    let triggered = false;

    const loop = (time) => {
      // 취소되었다면 루프를 즉시 종료
      if (isCancelledRef.current) {
        stopSilenceLoop();
        return;
      }

      analyser.getByteFrequencyData(dataArray);
      const volume = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;

      if (time - silenceStart < ignoreInitialSilence) {
        silenceLoopRef.current = requestAnimationFrame(loop);
        return;
      }

      if (volume < silenceThreshold) {
        if (!triggered) {
          silenceStart = time;
          triggered = true;
        }
        if (time - silenceStart > silenceTimeout) {
          onSilence?.();
          return;
        }
      } else {
        triggered = false;
      }

      silenceLoopRef.current = requestAnimationFrame(loop);
    };

    silenceLoopRef.current = requestAnimationFrame(loop);
  };

  const stopSilenceLoop = () => {
    if (silenceLoopRef.current) {
      cancelAnimationFrame(silenceLoopRef.current);
      silenceLoopRef.current = null;
    }
  };

  const stopVoiceLoop = () => {
    voiceLoopRef.current = false;
  };

  const startRecording = () => {
    cleanup();
    stopSilenceLoop();
    stopVoiceLoop();
    clearTimeout(maxTimeoutRef.current);
    
    isSpokenRef.current = false;
    isCancelledRef.current = false; // 시작 시 취소 플래그 초기화

    return new Promise(async (resolve, reject) => {
      startPromiseResolveRef.current = resolve;

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        
        // 권한 허용 후 그 사이에 취소 버튼을 눌렀을 경우 대비
        if (isCancelledRef.current) {
          stream.getTracks().forEach(t => t.stop());
          resolve(null);
          return;
        }

        await startBaseRecording(stream);
        detectVoice();

        // 자동 종료 시 호출되는 공통 함수
        const stopAndResolve = async () => {
          try {
            const result = await stopRecording(); // stopRecording() 결과를 받아
            resolve(result); // startRecording()의 Promise를 해결(resolve), result가 null이면 그대로 전달
          } catch (err) {
            reject(err);
          }
        };

         // 무음 감지 시 자동 종료
        if (autoStopOnSilence) detectSilence(stopAndResolve);

        // 최대 녹음 시간 초과 시 자동 종료
        if (maxRecordTime > 0)
          maxTimeoutRef.current = setTimeout(stopAndResolve, maxRecordTime);

        onStatusChange?.("recording");
      } catch (err) {
        console.error("녹음 실패:", err);
        onStatusChange?.("error");
        reject(err);
      }
    });
  };

  const stopRecording = async () => {
    if (isCancelledRef.current) return null;

    stopSilenceLoop();
    stopVoiceLoop();
    clearTimeout(maxTimeoutRef.current);
    onStatusChange?.("processing");

    let blob;
    try {
      blob = await stopBaseRecording();
      if (!blob) return null;
    } catch (err) {
      onStatusChange?.("error");
      return null;
    }

    if (!isSpokenRef.current) {
      // console.log("음성 감지 실패");
      onStatusChange?.("error");
      return null;
    }

    const formData = new FormData();
    const extension = blob.type.split("/")[1];
    formData.append("audioFile", blob, `recording.${extension}`);

    try {
      const data = await uploadAudio(formData);
      if (isCancelledRef.current) return null;
      onStatusChange?.("idle");
      return { audioBlob: blob, ...data };
    } catch (err) {
      console.error("업로드 실패:", err);
      onStatusChange?.("error");
      return null;
    } finally {
      cleanup();
    }
  };

  const cancelRecording = () => {
    isCancelledRef.current = true;
    
    stopSilenceLoop();
    stopVoiceLoop();

    if (maxTimeoutRef.current) {
      clearTimeout(maxTimeoutRef.current);
      maxTimeoutRef.current = null;
    }

    cleanup();

    onStatusChange?.("idle");

    if (startPromiseResolveRef.current) {
      startPromiseResolveRef.current(null);
      startPromiseResolveRef.current = null;
    }
  };

  return { startRecording, stopRecording, cancelRecording };
};